/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Directive, EventEmitter, Input, Output} from '@angular/core';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {SortDirection} from './sort-direction';
import {getMdSortDuplicateMdSortableIdError, getMdSortHeaderMissingIdError} from './sort-errors';

export interface MdSortable {
  id: string;
  start: 'asc' | 'desc';
  disableClear: boolean;
}

export interface Sort {
  active: string;
  direction: SortDirection;
}
/**
 * Container for MdSortables to manage the sort state and provide default sort parameters.
 */
export class MdSort {
/**
 * Collection of all registered sortables that this directive manages.
 */
sortables = new Map<string, MdSortable>();
/**
 * The id of the most recently sorted MdSortable.
 */
active: string;
/**
 * The direction to set when an MdSortable is initially sorted.
 * May be overriden by the MdSortable's sort start.
 */
start: 'asc' | 'desc' = 'asc';
/**
 * The sort direction of the currently active MdSortable.
 */
direction: SortDirection = '';
/**
 * Whether to disable the user from clearing the sort by finishing the sort direction cycle.
 * May be overriden by the MdSortable's disable clear input.
 * @return {?}
 */
get disableClear() { return this._disableClear; }
/**
 * @param {?} v
 * @return {?}
 */
set disableClear(v) { this._disableClear = coerceBooleanProperty(v); }
private _disableClear: boolean;
/**
 * @return {?}
 */
get _matSortActive() { return this.active; }
/**
 * @param {?} v
 * @return {?}
 */
set _matSortActive(v) { this.active = v; }
/**
 * @return {?}
 */
get _matSortStart() { return this.start; }
/**
 * @param {?} v
 * @return {?}
 */
set _matSortStart(v) { this.start = v; }
/**
 * @return {?}
 */
get _matSortDirection() { return this.direction; }
/**
 * @param {?} v
 * @return {?}
 */
set _matSortDirection(v) { this.direction = v; }
/**
 * @return {?}
 */
get _matSortDisableClear() { return this.disableClear; }
/**
 * @param {?} v
 * @return {?}
 */
set _matSortDisableClear(v) { this.disableClear = v; }
/**
 * Event emitted when the user changes either the active sort or sort direction.
 */
readonly sortChange = new EventEmitter<Sort>();
/**
 * @return {?}
 */
get _matSortChange(): EventEmitter<Sort> { return this.sortChange; }
/**
 * Register function to be used by the contained MdSortables. Adds the MdSortable to the
 * collection of MdSortables.
 * @param {?} sortable
 * @return {?}
 */
register(sortable: MdSortable) {
    if (!sortable.id) {
      throw getMdSortHeaderMissingIdError();
    }

    if (this.sortables.has(sortable.id)) {
      throw getMdSortDuplicateMdSortableIdError(sortable.id);
    }
    this.sortables.set(sortable.id, sortable);
  }
/**
 * Unregister function to be used by the contained MdSortables. Removes the MdSortable from the
 * collection of contained MdSortables.
 * @param {?} sortable
 * @return {?}
 */
deregister(sortable: MdSortable) {
    this.sortables.delete(sortable.id);
  }
/**
 * Sets the active sort id and determines the new sort direction.
 * @param {?} sortable
 * @return {?}
 */
sort(sortable: MdSortable) {
    if (this.active != sortable.id) {
      this.active = sortable.id;
      this.direction = sortable.start ? sortable.start : this.start;
    } else {
      this.direction = this.getNextSortDirection(sortable);
    }

    this.sortChange.next({active: this.active, direction: this.direction});
  }
/**
 * Returns the next sort direction of the active sortable, checking for potential overrides.
 * @param {?} sortable
 * @return {?}
 */
getNextSortDirection(sortable: MdSortable): SortDirection {
    if (!sortable) { return ''; }

    // Get the sort direction cycle with the potential sortable overrides.
    const /** @type {?} */ disableClear = sortable.disableClear != null ? sortable.disableClear : this.disableClear;
    let /** @type {?} */ sortDirectionCycle = getSortDirectionCycle(sortable.start || this.start, disableClear);

    // Get and return the next direction in the cycle
    let /** @type {?} */ nextDirectionIndex = sortDirectionCycle.indexOf(this.direction) + 1;
    if (nextDirectionIndex >= sortDirectionCycle.length) { nextDirectionIndex = 0; }
    return sortDirectionCycle[nextDirectionIndex];
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[mdSort], [matSort]',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'active': [{ type: Input, args: ['mdSortActive', ] },],
'start': [{ type: Input, args: ['mdSortStart', ] },],
'direction': [{ type: Input, args: ['mdSortDirection', ] },],
'disableClear': [{ type: Input, args: ['mdSortDisableClear', ] },],
'_matSortActive': [{ type: Input, args: ['matSortActive', ] },],
'_matSortStart': [{ type: Input, args: ['matSortStart', ] },],
'_matSortDirection': [{ type: Input, args: ['matSortDirection', ] },],
'_matSortDisableClear': [{ type: Input, args: ['matSortDisableClear', ] },],
'sortChange': [{ type: Output, args: ['mdSortChange', ] },],
'_matSortChange': [{ type: Output, args: ['matSortChange', ] },],
};
}

function MdSort_tsickle_Closure_declarations() {
/** @type {?} */
MdSort.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdSort.ctorParameters;
/** @type {?} */
MdSort.propDecorators;
/**
 * Collection of all registered sortables that this directive manages.
 * @type {?}
 */
MdSort.prototype.sortables;
/**
 * The id of the most recently sorted MdSortable.
 * @type {?}
 */
MdSort.prototype.active;
/**
 * The direction to set when an MdSortable is initially sorted.
 * May be overriden by the MdSortable's sort start.
 * @type {?}
 */
MdSort.prototype.start;
/**
 * The sort direction of the currently active MdSortable.
 * @type {?}
 */
MdSort.prototype.direction;
/** @type {?} */
MdSort.prototype._disableClear;
/**
 * Event emitted when the user changes either the active sort or sort direction.
 * @type {?}
 */
MdSort.prototype.sortChange;
}

/**
 * Returns the sort direction cycle to use given the provided parameters of order and clear.
 * @param {?} start
 * @param {?} disableClear
 * @return {?}
 */
function getSortDirectionCycle(start: 'asc' | 'desc',
                               disableClear: boolean): SortDirection[] {
  let /** @type {?} */ sortOrder: SortDirection[] = ['asc', 'desc'];
  if (start == 'desc') { sortOrder.reverse(); }
  if (!disableClear) { sortOrder.push(''); }

  return sortOrder;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
